@if (isLoading) {
  <div class="loader-overlay" role="status" aria-live="polite">
    <div class="heart-wrapper" aria-hidden="true">
      <svg class="heart-svg" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path
          class="heart-shape"
          d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 6 4 4 6.5 4 8.24 4 9.91 5 10.54 6.36
               11.17 5 12.76 4 14.5 4 17 4 19 6 19 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"
        />
        <polyline class="ekg" points="3 12 7 12 9 8 11 15 13 6 15 12 21 12" fill="none" />
      </svg>
    </div>
    <p class="loader-text mt-3 fw-semibold text-dark">Registering practice...</p>
  </div>
}

@if (!isLoading) {
  <div class="container py-4" style="max-width: 960px">
    <!-- Hero Banner -->
    <div class="booking-hero card border-0 mb-4">
      <div class="booking-hero__content">
        <div>
          <div class="booking-hero__eyebrow">Medical Practice Setup</div>
          <h2 class="booking-hero__title mb-1">Practice Onboarding</h2>
          <div class="booking-hero__meta">
            <span
              ><i class="bi bi-hospital me-1 text-primary"></i>Establish practice identity, physical
              location, and root manager credentials</span
            >
          </div>
        </div>
      </div>
    </div>

    <!-- Prominent Error Alert Banner -->
    @if (errorMessage) {
      <div
        class="alert alert-danger d-flex align-items-start mb-4 p-3 rounded-3 shadow-sm border-danger border-start border-4"
        role="alert"
      >
        <i class="bi bi-exclamation-octagon-fill fs-4 me-3 text-danger flex-shrink-0 mt-1"></i>
        <div class="flex-grow-1">
          <div class="fw-bold text-danger">Registration Error</div>
          <div class="small text-dark mt-1">{{ errorMessage }}</div>
        </div>
        <button
          type="button"
          class="btn-close ms-2"
          (click)="errorMessage = null"
          aria-label="Close error message"
        ></button>
      </div>
    }

    <form [formGroup]="form" (ngSubmit)="onSubmit()" novalidate>
      <!-- Section 1: Practice Identity & Address -->
      <div class="card booking-card mb-4">
        <div class="card-header booking-card__header">
          <div>
            <div class="booking-card__eyebrow">Step 1</div>
            <h4 class="sub-title mb-0">Practice Details & Address</h4>
          </div>
        </div>

        <div class="card-body p-4">
          <div class="row g-3">
            <div class="col-12">
              <label for="name" class="form-label"
                >Practice Name <span class="text-danger">*</span></label
              >
              <input
                id="name"
                type="text"
                class="form-control"
                formControlName="name"
                placeholder="e.g. Sunrise Medical Centre"
              />
              @if (form.controls['name'].touched && form.controls['name'].errors?.['required']) {
                <div class="text-danger small mt-1">Practice name is required.</div>
              }
              @if (form.controls['name'].touched && form.controls['name'].errors?.['maxlength']) {
                <div class="text-danger small mt-1">
                  Practice name must not exceed 150 characters.
                </div>
              }
            </div>

            <div class="col-12 mt-4">
              <div class="section-eyebrow mb-1">Physical Location</div>
            </div>

            <div class="col-12">
              <label for="addressLine1" class="form-label"
                >Address Line 1 <span class="text-danger">*</span></label
              >
              <input
                id="addressLine1"
                type="text"
                class="form-control"
                formControlName="addressLine1"
                placeholder="Building and street address"
              />
              @if (
                form.controls['addressLine1'].touched &&
                form.controls['addressLine1'].errors?.['required']
              ) {
                <div class="text-danger small mt-1">Address Line 1 is required.</div>
              }
            </div>

            <div class="col-md-6">
              <label for="addressLine2" class="form-label">Address Line 2</label>
              <input
                id="addressLine2"
                type="text"
                class="form-control"
                formControlName="addressLine2"
                placeholder="Suite, unit, floor (optional)"
              />
            </div>

            <div class="col-md-6">
              <label for="addressLine3" class="form-label">Address Line 3</label>
              <input
                id="addressLine3"
                type="text"
                class="form-control"
                formControlName="addressLine3"
                placeholder="Suburb / area (optional)"
              />
            </div>

            <div class="col-md-6">
              <label for="city" class="form-label">City <span class="text-danger">*</span></label>
              <input
                id="city"
                type="text"
                class="form-control"
                formControlName="city"
                placeholder="e.g. Cape Town"
              />
              @if (form.controls['city'].touched && form.controls['city'].errors?.['required']) {
                <div class="text-danger small mt-1">City is required.</div>
              }
            </div>

            <div class="col-md-6">
              <label for="postalCode" class="form-label"
                >Postal Code <span class="text-danger">*</span></label
              >
              <input
                id="postalCode"
                type="text"
                class="form-control"
                formControlName="postalCode"
                placeholder="e.g. 8001"
              />
              @if (
                form.controls['postalCode'].touched &&
                form.controls['postalCode'].errors?.['required']
              ) {
                <div class="text-danger small mt-1">Postal code is required.</div>
              }
            </div>
          </div>
        </div>
      </div>

      <!-- Section 2: Manager Account -->
      <div class="card booking-card mb-4">
        <div class="card-header booking-card__header">
          <div>
            <div class="booking-card__eyebrow">Step 2</div>
            <h4 class="sub-title mb-0">Primary Practice Manager Account</h4>
          </div>
        </div>

        <div class="card-body p-4">
          <p class="text-muted small mb-3">
            This account will hold full administrative ownership of the practice.
          </p>

          <div class="row g-3">
            <div class="col-md-6">
              <label for="managerFirstName" class="form-label"
                >First Name <span class="text-danger">*</span></label
              >
              <input
                id="managerFirstName"
                type="text"
                class="form-control"
                formControlName="managerFirstName"
                placeholder="e.g. John"
              />
              @if (
                form.controls['managerFirstName'].touched &&
                form.controls['managerFirstName'].errors?.['required']
              ) {
                <div class="text-danger small mt-1">First name is required.</div>
              }
            </div>

            <div class="col-md-6">
              <label for="managerLastName" class="form-label"
                >Last Name <span class="text-danger">*</span></label
              >
              <input
                id="managerLastName"
                type="text"
                class="form-control"
                formControlName="managerLastName"
                placeholder="e.g. Smith"
              />
              @if (
                form.controls['managerLastName'].touched &&
                form.controls['managerLastName'].errors?.['required']
              ) {
                <div class="text-danger small mt-1">Last name is required.</div>
              }
            </div>

            <div class="col-md-6">
              <label for="managerCellNumber" class="form-label"
                >Cell Number <span class="text-danger">*</span></label
              >
              <input
                id="managerCellNumber"
                type="tel"
                class="form-control"
                formControlName="managerCellNumber"
                placeholder="e.g. 0821234567"
              />
              @if (
                form.controls['managerCellNumber'].touched &&
                form.controls['managerCellNumber'].errors?.['required']
              ) {
                <div class="text-danger small mt-1">Cell number is required.</div>
              }
            </div>

            <div class="col-md-6">
              <label for="managerEmail" class="form-label"
                >Email <span class="text-danger">*</span></label
              >
              <input
                id="managerEmail"
                type="email"
                class="form-control"
                formControlName="managerEmail"
                placeholder="e.g. manager@practice.com"
              />
              @if (
                form.controls['managerEmail'].touched &&
                form.controls['managerEmail'].errors?.['required']
              ) {
                <div class="text-danger small mt-1">Email is required.</div>
              }
            </div>

            <div class="col-md-12">
              <label for="managerPassword" class="form-label"
                >Initial Password <span class="text-danger">*</span></label
              >
              <input
                id="managerPassword"
                type="password"
                class="form-control"
                formControlName="managerPassword"
                placeholder="Min. 8 characters with uppercase, lowercase, digit & special char"
              />
              @if (
                form.controls['managerPassword'].touched &&
                form.controls['managerPassword'].errors?.['required']
              ) {
                <div class="text-danger small mt-1">Password is required.</div>
              }
              @if (form.controls['managerPassword'].dirty) {
                <ul class="small mt-2 mb-0 ps-3 list-unstyled">
                  <li [class.text-success]="form.controls['managerPassword'].value?.length >= 8" [class.text-muted]="!(form.controls['managerPassword'].value?.length >= 8)">
                    <i class="bi" [class.bi-check-circle-fill]="form.controls['managerPassword'].value?.length >= 8" [class.bi-circle]="!(form.controls['managerPassword'].value?.length >= 8)"></i>
                    At least 8 characters
                  </li>
                  <li [class.text-success]="!form.controls['managerPassword'].errors?.['missingUppercase']" [class.text-muted]="!!form.controls['managerPassword'].errors?.['missingUppercase']">
                    <i class="bi" [class.bi-check-circle-fill]="!form.controls['managerPassword'].errors?.['missingUppercase']" [class.bi-circle]="!!form.controls['managerPassword'].errors?.['missingUppercase']"></i>
                    At least one uppercase letter
                  </li>
                  <li [class.text-success]="!form.controls['managerPassword'].errors?.['missingLowercase']" [class.text-muted]="!!form.controls['managerPassword'].errors?.['missingLowercase']">
                    <i class="bi" [class.bi-check-circle-fill]="!form.controls['managerPassword'].errors?.['missingLowercase']" [class.bi-circle]="!!form.controls['managerPassword'].errors?.['missingLowercase']"></i>
                    At least one lowercase letter
                  </li>
                  <li [class.text-success]="!form.controls['managerPassword'].errors?.['missingDigit']" [class.text-muted]="!!form.controls['managerPassword'].errors?.['missingDigit']">
                    <i class="bi" [class.bi-check-circle-fill]="!form.controls['managerPassword'].errors?.['missingDigit']" [class.bi-circle]="!!form.controls['managerPassword'].errors?.['missingDigit']"></i>
                    At least one digit
                  </li>
                  <li [class.text-success]="!form.controls['managerPassword'].errors?.['missingSpecial']" [class.text-muted]="!!form.controls['managerPassword'].errors?.['missingSpecial']">
                    <i class="bi" [class.bi-check-circle-fill]="!form.controls['managerPassword'].errors?.['missingSpecial']" [class.bi-circle]="!!form.controls['managerPassword'].errors?.['missingSpecial']"></i>
                    At least one special character (!&#64;#$%...)
                  </li>
                </ul>
              }
            </div>
          </div>
        </div>
      </div>

      <!-- Actions Sticky Bar -->
      <div class="actions actions--sticky d-flex justify-content-between align-items-center">
        <small class="text-muted"
          >Fields marked with <span class="text-danger">*</span> are mandatory.</small
        >
        <button type="submit" class="btn btn-primary px-4" [disabled]="form.invalid">
          <i class="bi bi-check2-circle me-1"></i> Register Practice
        </button>
      </div>
    </form>
  </div>
}
